import React from 'react';
import { Typography } from '@mui/material';
import { EnhancedTableFooterProps } from '../../types/footer-types';
import { useTableFooter } from '../../hooks/use-table-footer';
import {
  StyledTableFooter,
  StyledFooterRow,
  StyledFooterCell,
  StyledFooterContent,
  StyledSummaryContainer,
  StyledSummaryItem,
} from '../styled/table-footer-styles';

/**
 * Enhanced Table Footer Component
 *
 * Displays summary information at the bottom of a table, including:
 * - Total row count with customizable label
 * - Summary values for specified columns with user-defined calculations
 * - Consistent styling that matches table theme
 * - Optional sticky positioning
 *
 * This component is designed to be used within the table structure,
 * positioned between the table body and pagination.
 *
 * ## Features
 *
 * - **Automatic Calculations**: Built-in helpers for sum, average, min, max, count
 * - **Custom Calculations**: User-defined callback functions for complex aggregations
 * - **Responsive Design**: Adapts to different screen sizes and table layouts
 * - **Theme Integration**: Matches table styling with hover-like background
 * - **Accessibility**: Proper ARIA labels and semantic HTML structure
 * - **Performance**: Memoized calculations to prevent unnecessary re-renders
 *
 * @example
 * ```tsx
 * <EnhancedTableFooter
 *   rows={filteredData}
 *   columns={tableColumns}
 *   config={{
 *     enabled: true,
 *     totalRowsLabel: "Total Desserts",
 *     summaryColumns: [
 *       {
 *         columnId: 'calories',
 *         label: 'Total Calories',
 *         callback: (rows, values, helpers) => helpers.sum(values)
 *       },
 *       {
 *         columnId: 'price',
 *         label: 'Average Price',
 *         callback: (rows, values, helpers) =>
 *           `$${helpers.formatNumber(helpers.average(values), { minimumFractionDigits: 2 })}`
 *       }
 *     ]
 *   }}
 *   dense={false}
 *   showSelection={true}
 * />
 * ```
 */
export function EnhancedTableFooter<T extends Record<string, unknown>>(
  props: EnhancedTableFooterProps<T>,
): React.ReactElement | null {
  const {
    rows,
    columns,
    config,
    dense: _dense = false,
    showSelection = false,
    showExpansion = false,
    className,
  } = props;

  const { summaryValues, totalRowsCount, totalRowsLabel } = useTableFooter({
    rows,
    config,
  });

  // Don't render if footer is disabled or no configuration
  if (
    !config.enabled ||
    (!config.summaryColumns.length && !config.totalRowsLabel)
  ) {
    return null;
  }

  // Calculate column spans and positioning
  const totalColumns =
    columns.length + (showSelection ? 1 : 0) + (showExpansion ? 1 : 0);
  const leftColumnSpan =
    showSelection && showExpansion ? 2 : showSelection || showExpansion ? 1 : 0;

  // Create a map of column IDs to their positions for alignment
  const columnPositions = new Map<keyof T, number>();
  let currentPosition = leftColumnSpan;

  columns.forEach((column) => {
    columnPositions.set(column.id, currentPosition);
    currentPosition++;
  });

  const footerContent = (
    <StyledTableFooter
      className={className}
      sx={
        config.sticky
          ? {
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
            }
          : undefined
      }
    >
      <StyledFooterRow>
        {/* Single cell spanning all columns for better full-width display */}
        <StyledFooterCell colSpan={totalColumns} className="footer-total">
          <StyledFooterContent>
            {/* Left side - Total rows count */}
            <Typography variant="body2" component="span">
              <span className="total-label">{totalRowsLabel}:</span>{' '}
              <span className="total-value">{totalRowsCount}</span>
            </Typography>

            {/* Right side - Summary values */}
            {summaryValues.length > 0 && (
              <StyledSummaryContainer>
                {summaryValues.map((summary, index) => (
                  <StyledSummaryItem
                    key={`${String(summary.columnId)}-${index}`}
                  >
                    <Typography variant="body2" component="span">
                      <span className="summary-label">{summary.label}:</span>{' '}
                      <span className="summary-value">{summary.value}</span>
                    </Typography>
                  </StyledSummaryItem>
                ))}
              </StyledSummaryContainer>
            )}
          </StyledFooterContent>
        </StyledFooterCell>
      </StyledFooterRow>
    </StyledTableFooter>
  );

  return footerContent;
}

EnhancedTableFooter.displayName = 'EnhancedTableFooter';
